import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Users, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface UsersByDay {
  date: string;
  count: number;
  cumulative: number;
}

interface UserGrowthChartProps {
  data: UsersByDay[];
  totalUsers: number;
  isLoading?: boolean;
}

const chartConfig = {
  cumulative: {
    label: 'Total Usuários',
    color: 'hsl(var(--chart-3))',
  },
  count: {
    label: 'Novos',
    color: 'hsl(var(--chart-4))',
  },
};

export function UserGrowthChart({ data, totalUsers, isLoading }: UserGrowthChartProps) {
  const formattedData = data.map(item => ({
    ...item,
    dateLabel: format(parseISO(item.date), 'dd/MM', { locale: ptBR }),
  }));

  const newUsers = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Crescimento de Usuários
            </CardTitle>
            <CardDescription>Evolução do número de usuários</CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{totalUsers}</p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500 font-medium">+{newUsers}</span>
              <span>no período</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : data.length === 0 || data.every(d => d.cumulative === 0) ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum dado no período selecionado
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[250px] w-full">
            <LineChart data={formattedData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="dateLabel" 
                tickLine={false} 
                axisLine={false}
                tickMargin={8}
                interval="preserveStartEnd"
              />
              <YAxis 
                tickLine={false} 
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
              />
              <ChartTooltip 
                content={<ChartTooltipContent />}
                labelFormatter={(value) => value}
              />
              <Line
                type="monotone"
                dataKey="cumulative"
                stroke="hsl(var(--chart-3))"
                strokeWidth={2}
                dot={{ fill: 'hsl(var(--chart-3))', strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
